import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import type { OnboardingStep } from '@/lib/stores/onboarding';

const STEPS: { key: OnboardingStep; i18nKey: string }[] = [
  { key: 'identity', i18nKey: 'steps.identity' },
  { key: 'banking',  i18nKey: 'steps.banking'  },
  { key: 'services', i18nKey: 'steps.services' },
  { key: 'voice',    i18nKey: 'steps.voice'    },
];

const STEP_INDEX: Record<OnboardingStep, number> = {
  identity: 0,
  banking:  1,
  services: 2,
  voice:    3,
};

interface StepperProps {
  currentStep: OnboardingStep;
}

export function Stepper({ currentStep }: StepperProps) {
  const { t } = useTranslation('onboarding');
  const currentIdx = STEP_INDEX[currentStep];

  return (
    <nav aria-label="Koraki onboardinga">
      <ol className="flex items-center gap-0">
        {STEPS.map((step, idx) => {
          const isCompleted = idx < currentIdx;
          const isActive    = idx === currentIdx;

          return (
            <li key={step.key} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-1.5">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium border-2 transition-colors',
                    isCompleted && 'bg-copper border-copper text-parchment',
                    isActive    && 'bg-white border-copper text-copper',
                    !isCompleted && !isActive && 'bg-white border-linen text-steel'
                  )}
                  aria-current={isActive ? 'step' : undefined}
                >
                  {isCompleted ? (
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  ) : (
                    <span>{idx + 1}</span>
                  )}
                </div>
                <span
                  className={cn(
                    'text-xs whitespace-nowrap hidden sm:block',
                    isActive    && 'text-copper font-medium',
                    isCompleted && 'text-iron',
                    !isActive && !isCompleted && 'text-steel'
                  )}
                >
                  {t(step.i18nKey)}
                </span>
              </div>

              {idx < STEPS.length - 1 && (
                <div
                  className={cn(
                    'h-0.5 flex-1 mx-2 mb-5 sm:mb-0 transition-colors',
                    isCompleted ? 'bg-copper' : 'bg-linen'
                  )}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
